import ColumnFilter from 'components/ui/Table/Filters/ColumnFilter'
import DateRangeColumnFilter from 'components/ui/Table/Filters/DateRangeColumnFilter'
import Table from 'components/ui/Table/Table'
import { useModal } from 'contexts/ModalContext'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { ImSpinner9 } from 'react-icons/im'
import { LuEye, LuSquarePen, LuTrash2 } from 'react-icons/lu'
import { Link } from 'wouter'

export default function InjuredCompGrid({
  user,
  data,
  handleRefresh,
  handleAnalyzeExcel,
  handleDelete
}) {
  // Hook para modal
  const { modal, openModal } = useModal()

  // Estado para datos formateados de la tabla
  const [getTableData, setTableData] = useState(null)

  // Ordenación personalizada para fechas
  const customDateSort = (rowA, rowB, columnId) => {
    let dateA = null
    let dateB = null

    // Convertir fecha de rowA en un objeto 'Date'
    if (
      typeof rowA?.values[columnId] === 'string' &&
      rowA?.values[columnId] !== '' &&
      rowA?.values[columnId] !== null &&
      rowA?.values[columnId] !== undefined
    ) {
      const parts = rowA?.values[columnId].split('/')

      if (parts.length === 3) {
        let [day, month, year] = parts.map((num) => parseInt(num, 10))
        year = year < 100 ? 2000 + year : year
        dateA = new Date(Date.UTC(year, month - 1, day, 0, 0, 0))
      }
    }

    // Convertir fecha de rowB en un objeto 'Date'
    if (
      typeof rowB?.values[columnId] === 'string' &&
      rowB?.values[columnId] !== '' &&
      rowB?.values[columnId] !== null &&
      rowB?.values[columnId] !== undefined
    ) {
      const parts = rowB?.values[columnId].split('/')

      if (parts.length === 3) {
        let [day, month, year] = parts.map((num) => parseInt(num, 10))
        year = year < 100 ? 2000 + year : year
        dateB = new Date(Date.UTC(year, month - 1, day, 0, 0, 0))
      }
    }

    // Comparar fechas
    if (isNaN(dateA)) return 1
    if (isNaN(dateB)) return -1

    return dateA - dateB
  }

  // Columnas:
  const columns = [
    {
      title: 'Filtrar por -Estatus-',
      Header: 'Estatus',
      accessor: 'Injured_Status',
      Filter: ColumnFilter,
      showInPDF: true,
      showInExcel: true
    },
    {
      title: 'Filtrar por -Nº de Siniestro-',
      Header: 'Nº de Siniestro',
      accessor: 'Injured_Incident_Number',
      Filter: ColumnFilter,
      showInPDF: true,
      showInExcel: true
    },
    {
      title: 'Filtrar por -Referencia agencia-',
      Header: 'Referencia agencia',
      accessor: 'Injured_Agency_Reference',
      Filter: ColumnFilter,
      showInPDF: true,
      showInExcel: true
    },
    {
      title: 'Filtrar por -Referencia TUS-',
      Header: 'Referencia TUS',
      accessor: 'Injured_TUS_Reference',
      Filter: ColumnFilter,
      showInPDF: true,
      showInExcel: true
    },
    {
      title: 'Filtrar por -Riesgo siniestro-',
      Header: 'Riesgo siniestro',
      accessor: 'Injured_Incident_Risk',
      Filter: ColumnFilter,
      showInPDF: true,
      showInExcel: true
    },
    {
      title: 'Filtrar por -F. Ocurrencia-',
      Header: 'F. Ocurrencia',
      accessor: 'Injured_Ocurrence_Date',
      Filter: DateRangeColumnFilter,
      filter: 'dateBetween', // Filtro de fechas entre
      sortType: customDateSort, // Función de ordenación personalizada para fechas
      showInPDF: true,
      showInExcel: true
    },
    {
      Header: 'Hora Ocurrencia', // Esta columna no se muestra en la tabla, pero se incluye para que aparezca al exportar a Excel
      accessor: 'Injured_Occurrence_Time',
      showInPDF: false,
      showInExcel: true
    },
    {
      Header: 'Coordenadas', // Esta columna no se muestra en la tabla, pero se incluye para que aparezca al exportar a Excel
      accessor: 'Injured_Coordinates',
      showInPDF: false,
      showInExcel: true
    },
    {
      title: 'Filtrar por -Conductor-',
      Header: 'Conductor',
      accessor: 'Injured_Driver',
      Filter: ColumnFilter,
      showInPDF: true,
      showInExcel: true
    },
    {
      Header: 'Pagos', // Esta columna no se muestra en la tabla, pero se incluye para que aparezca al exportar a Excel
      accessor: 'Injured_Payments',
      showInPDF: false,
      showInExcel: true
    },
    {
      Header: 'Descripción', // Esta columna no se muestra en la tabla, pero se incluye para que aparezca al exportar a Excel
      accessor: 'Injured_Description',
      showInPDF: false,
      showInExcel: true
    },
    {
      Header: 'Observaciones', // Esta columna no se muestra en la tabla, pero se incluye para que aparezca al exportar a Excel
      accessor: 'Injured_Observations',
      showInPDF: false,
      showInExcel: true
    },
    {
      title: 'Acciones',
      Header: '',
      accessor: 'Injured_Actions',
      disableSortBy: true, // Deshabilitar ordenación
      Cell: (value) => {
        return (
          <div className="float-right px-4 py-4 text-sm whitespace-nowrap">
            <div className="flex items-center gap-x-6">
              {/* Enlace para editar o ver el registro según el rol */}
              <Link
                title={user.role_id === 1 ? 'Editar registro' : 'Ver registro'}
                href={`/tus-incidents/injured/${value?.cell?.row?.original?.Injured_Id}`}
                className="text-gray-500 transition-colors duration-200 dark:hover:text-yellow-500 dark:text-gray-300 hover:text-yellow-500 focus:outline-none"
              >
                {user.role_id === 1 ? (
                  <LuSquarePen size={20} />
                ) : (
                  <LuEye size={20} />
                )}
              </Link>

              {/* Botón para eliminar el registro (visible solo si es admin) */}
              {user.role_id === 1 ? (
                <button
                  title="Eliminar registro"
                  type="button"
                  className="text-gray-500 transition-colors duration-200 dark:hover:text-red-500 dark:text-gray-300 hover:text-red-500 focus:outline-none"
                  onClick={() =>
                    openModal('DeleteModal', {
                      itemName:
                        value?.cell?.row?.original?.Injured_Incident_Number,
                      itemId: value?.cell?.row?.original?.Injured_Id,
                      handleDelete
                    })
                  }
                >
                  <LuTrash2 size={20} />
                </button>
              ) : null}
            </div>
          </div>
        )
      },
      showInPDF: false,
      showInExcel: false
    }
  ]

  // Adaptar datos para la tabla, agregar prefijo al campo para evitar conflictos con otras tablas
  useEffect(() => {
    if (data) {
      const itemsData = []
      data?.forEach((item) => {
        // IMPORTANTE: Ordenar estos atributos igual que las columnas de la tabla para evitar desajustes en la exportación Excel/PDF
        itemsData.push({
          Injured_Id: item.id,
          Injured_Status: item.status,
          Injured_Incident_Number: item.incident_number,
          Injured_Agency_Reference: item.agency_reference,
          Injured_TUS_Reference: item.tus_reference,
          Injured_Incident_Risk: item.incident_risk,
          Injured_Ocurrence_Date: item.occurrence_date,
          Injured_Occurrence_Time: item.occurrence_time,
          Injured_Coordinates: item.coordinates,
          Injured_Driver: item.driver,
          Injured_Payments: item.payments,
          Injured_Description: item.description,
          Injured_Observations: item.observations,
          Injured_Type: item.type
        })
      })
      setTableData(itemsData)
    }
  }, [data])

  return getTableData ? (
    <Table
      tableName={'Lesionados'}
      columns={columns}
      columnsToWrap={['Injured_Description', 'Injured_Observations']}
      createNewItemURL={'/tus-incidents/injured/new'}
      data={getTableData}
      defaultSort={{ id: 'Injured_Ocurrence_Date', desc: true }} // Ordenación por 'F. Ocurrencia' descendente por defecto
      hiddenColumns={[
        'Injured_Occurrence_Time',
        'Injured_Coordinates',
        'Injured_Payments',
        'Injured_Description',
        'Injured_Observations'
      ]}
      allowImportExcel={true}
      uploadExcelURL={`incidents/injured/${user.id}`}
      handleRefresh={handleRefresh}
      handleAnalyzeExcel={handleAnalyzeExcel}
    />
  ) : (
    <div className="flex items-center content-center justify-center w-full h-full">
      <p
        className="flex flex-col-reverse items-center justify-center h-full font-bold dark:text-[#F5F5F5] text-gray-700"
        title="Cargando..."
      >
        <ImSpinner9 size={70} className="animate-spin" />
      </p>
    </div>
  )
}

InjuredCompGrid.propTypes = {
  user: PropTypes.object,
  data: PropTypes.array,
  handleRefresh: PropTypes.func.isRequired,
  handleAnalyzeExcel: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired
}
